import React, { useEffect, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { ClipLoader } from "react-spinners";
import api from "../../services/api";
import { AUTOPILOT_COLORS } from "./styles";
import { countMatches, withSearch } from "./searchHighlight";

function formatXml(xml) {
  let formatted = "";
  let indent = 0;
  // Split on tag boundaries, keeping the delimiters
  const tokens = xml.replace(/>\s*</g, "><").split(/(?<=>)(?=<)/);
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i].trim();
    if (!token) continue;
    // Closing tag — dedent before printing
    if (/^<\//.test(token)) {
      indent = Math.max(0, indent - 1);
      formatted += "  ".repeat(indent) + token + "\n";
    // Self-closing or declaration/comment — no indent change
    } else if (/\/>$/.test(token) || /^<[?!]/.test(token)) {
      formatted += "  ".repeat(indent) + token + "\n";
    // Opening tag
    } else if (/^<[^/]/.test(token)) {
      // Check if this token contains both opening and closing (e.g. <tag>value</tag>)
      const openTag = (token.match(/^<([A-Za-z_][^>\s/]*)/) || [])[1];
      if (openTag && token.indexOf("</" + openTag + ">") !== -1) {
        // Inline: <tag>value</tag> — no indent change
        formatted += "  ".repeat(indent) + token + "\n";
      } else {
        formatted += "  ".repeat(indent) + token + "\n";
        indent++;
      }
    } else {
      formatted += "  ".repeat(indent) + token + "\n";
    }
  }
  return formatted;
}

function highlightXml(raw, query) {
  // Split by tokens we care about, preserving them
  // We'll build spans from regex replacements
  const result = [];
  let key = 0;

  // Process line by line for simplicity
  const lines = raw.split("\n");
  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    // Tokenize the line
    const tokens = [];
    let remaining = line;
    while (remaining.length > 0) {
      // Prolog
      const prologMatch = /^<\?[^?]*\?>/.exec(remaining);
      if (prologMatch) {
        tokens.push({ type: "prolog", text: prologMatch[0] });
        remaining = remaining.slice(prologMatch[0].length);
        continue;
      }
      // Comment
      const commentMatch = /^<!--[\s\S]*?-->/.exec(remaining);
      if (commentMatch) {
        tokens.push({ type: "comment", text: commentMatch[0] });
        remaining = remaining.slice(commentMatch[0].length);
        continue;
      }
      // Closing tag
      const closingMatch = /^<\/[A-Za-z_][\w.:]*>/.exec(remaining);
      if (closingMatch) {
        tokens.push({ type: "tag", text: closingMatch[0] });
        remaining = remaining.slice(closingMatch[0].length);
        continue;
      }
      // Opening/self-closing tag with attributes
      const openMatch = /^<([A-Za-z_][\w.:]*)((?:\s+[^>]*)?)(\s*\/?>)/.exec(remaining);
      if (openMatch) {
        // tag name part
        tokens.push({ type: "tag", text: "<" + openMatch[1] });
        // attributes part
        if (openMatch[2]) {
          // parse attributes within this segment
          let attrStr = openMatch[2];
          while (attrStr.length > 0) {
            // attribute name
            const attrNameMatch = /^\s*([A-Za-z_][\w.:]*)\s*=\s*/.exec(attrStr);
            if (attrNameMatch) {
              tokens.push({ type: "attr", text: attrStr.slice(0, attrNameMatch[0].length - attrNameMatch[0].match(/=\s*$|=$/)[0].length) });
              attrStr = attrStr.slice(attrNameMatch[0].length - attrNameMatch[0].match(/=\s*$|=$/)[0].length);
              // equals sign
              const eqMatch = /^\s*=\s*/.exec(attrStr);
              if (eqMatch) {
                tokens.push({ type: "text-dim", text: eqMatch[0] });
                attrStr = attrStr.slice(eqMatch[0].length);
              }
              // attribute value
              const valMatch = /^"[^"]*"/.exec(attrStr);
              if (valMatch) {
                tokens.push({ type: "attrval", text: valMatch[0] });
                attrStr = attrStr.slice(valMatch[0].length);
              } else {
                // unquoted or not found — consume up to next space or >
                const fallback = /^[^\s>]*/.exec(attrStr);
                if (fallback) { tokens.push({ type: "attrval", text: fallback[0] }); attrStr = attrStr.slice(fallback[0].length); }
              }
            } else {
              tokens.push({ type: "text-dim", text: attrStr });
              attrStr = "";
            }
          }
        }
        tokens.push({ type: "tag", text: openMatch[3] });
        remaining = remaining.slice(openMatch[0].length);
        continue;
      }
      // Text content
      const textMatch = /^[^<]+/.exec(remaining);
      if (textMatch) {
        tokens.push({ type: "content", text: textMatch[0] });
        remaining = remaining.slice(textMatch[0].length);
        continue;
      }
      // Fallback: consume one char
      tokens.push({ type: "content", text: remaining[0] });
      remaining = remaining.slice(1);
    }

    for (const tok of tokens) {
      let color;
      if (tok.type === "prolog") color = "var(--everlog-code-prolog)";
      else if (tok.type === "comment") color = "var(--everlog-code-comment)";
      else if (tok.type === "tag") color = "var(--everlog-code-tag)";
      else if (tok.type === "attr") color = "var(--everlog-code-attr)";
      else if (tok.type === "attrval") color = "var(--everlog-code-attrval)";
      else if (tok.type === "text-dim") color = "var(--everlog-code-text-dim)";
      else color = "var(--everlog-code-text)";
      const kb = key++;
      result.push(<span key={kb} style={{ color }}>{query ? withSearch(tok.text, query, kb) : tok.text}</span>);
    }
    if (li < lines.length - 1) result.push(<br key={key++} />);
  }
  return result;
}

function XmlViewerModal({ isOpen, onClose, nomeArquivo, idTransportadora, embarcadorId, fetchUrl, fetchParams }) {
  const [loading, setLoading] = useState(false);
  const [xml, setXml] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState("");

  // Modo custom (ex.: NF no SFTP) busca de fetchUrl/fetchParams; sem isso, CT-e padrão.
  const usaCustom = !!fetchUrl;
  const customKey = usaCustom ? fetchUrl + "|" + JSON.stringify(fetchParams || {}) : "";

  useEffect(function() {
    if (!isOpen) return;
    if (usaCustom ? !nomeArquivo : (!nomeArquivo || !idTransportadora)) return;
    setLoading(true);
    setXml(null);
    setError(null);
    setSearch("");
    const url = usaCustom ? fetchUrl : "/autopilot/insights/cte/xml";
    let xmlParams;
    if (usaCustom) {
      xmlParams = fetchParams || {};
    } else {
      xmlParams = { nomeArquivo, idTransportadora };
      if (embarcadorId) xmlParams.embarcadorId = embarcadorId;
    }
    api.get(url, { params: xmlParams })
      .then(function(res) {
        setXml(res.data.xml);
        setLoading(false);
      })
      .catch(function(err) {
        setError((err.response && err.response.data && err.response.data.error) || "Erro ao carregar XML");
        setLoading(false);
      });
    // fetchUrl/fetchParams já entram pela dependência customKey (string estável).
    // Incluir fetchParams cru recriaria a dependência a cada render (objeto novo)
    // e dispararia re-fetch em loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, nomeArquivo, idTransportadora, embarcadorId, usaCustom, customKey]);

  function handleCopy() {
    if (!xml) return;
    navigator.clipboard.writeText(xml).then(function() {
      setCopied(true);
      setTimeout(function() { setCopied(false); }, 2000);
    });
  }

  function handleDownload() {
    // Modo custom (NF SFTP): o endpoint devolve só JSON {xml}; baixa do conteúdo já carregado.
    if (usaCustom) {
      if (!xml) return;
      const url = URL.createObjectURL(new Blob([xml], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = nomeArquivo || "nota.xml"; a.click();
      URL.revokeObjectURL(url);
      return;
    }
    const dlParams = { nomeArquivo, idTransportadora, download: "1" };
    if (embarcadorId) dlParams.embarcadorId = embarcadorId;
    api.get("/autopilot/insights/cte/xml", {
      params: dlParams,
      responseType: "blob",
    }).then(function(res) {
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = nomeArquivo; a.click();
      URL.revokeObjectURL(url);
    }).catch(function(err) {
      console.error("[Autopilot] Erro ao baixar XML:", err);
    });
  }

  const formattedXml = xml ? formatXml(xml) : "";
  const matchCount = countMatches(formattedXml, search);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="xl"
      fade={false}
      style={{ marginTop: "8vh", marginBottom: "8vh" }}
      contentClassName="cte-details-modal"
      backdropClassName="cte-modal-backdrop"
      wrapClassName="cte-modal-wrap"
    >
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "var(--everlog-card-hero-bg)",
        borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
        padding: "14px 20px",
      }}>
        <span style={{ color: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 80%, transparent)`, fontWeight: 600, fontSize: 14, fontFamily: "monospace" }}>
          {nomeArquivo || "XML"}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <input
            type="text"
            value={search}
            onChange={function(e) { setSearch(e.target.value); }}
            placeholder="Pesquisar no arquivo"
            disabled={!xml}
            style={{
              background: "var(--everlog-card-surface-field, var(--everlog-card-surface))",
              border: `1px solid ${AUTOPILOT_COLORS.border}`,
              borderRadius: "4px",
              color: AUTOPILOT_COLORS.title,
              fontSize: "0.75rem",
              padding: "3px 8px",
              width: 170,
            }}
          />
          {search ? (
            <span style={{ fontSize: "0.7rem", color: AUTOPILOT_COLORS.textSoft, whiteSpace: "nowrap" }}>
              {matchCount} {matchCount === 1 ? "ocorrência" : "ocorrências"}
            </span>
          ) : null}
          <button
            onClick={handleDownload}
            disabled={!xml}
            style={{
              background: "none",
              border: `1px solid ${AUTOPILOT_COLORS.border}`,
              borderRadius: "4px",
              color: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 80%, transparent)`,
              fontSize: "0.75rem",
              padding: "3px 10px",
              cursor: "pointer",
            }}
          >
            Baixar
          </button>
          <button
            onClick={handleCopy}
            disabled={!xml}
            style={{
              background: "none",
              border: `1px solid ${AUTOPILOT_COLORS.border}`,
              borderRadius: "4px",
              color: copied ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.textSoft,
              fontSize: "0.75rem",
              padding: "3px 10px",
              cursor: "pointer",
            }}
          >
            {copied ? "Copiado ✓" : "Copiar"}
          </button>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: AUTOPILOT_COLORS.title, fontSize: 24, lineHeight: 1, cursor: "pointer", padding: "0 4px" }}
          >
            ×
          </button>
        </div>
      </div>
      <ModalBody className="everlog-code-viewer" style={{ maxHeight: "65vh", overflow: "auto", padding: "16px 20px" }}>
        {loading && (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <ClipLoader size={28} color="var(--everlog-code-tag)" />
          </div>
        )}
        {error && (
          <p style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.85rem" }}>{error}</p>
        )}
        {xml && !loading && (
          <pre style={{ margin: 0, fontFamily: "'Fira Code', 'Courier New', monospace", fontSize: "0.78rem", lineHeight: "1.6", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {highlightXml(formattedXml, search)}
          </pre>
        )}
      </ModalBody>
    </Modal>
  );
}

export default XmlViewerModal;
